<template>
	<div v-if="isChildRoute">
		<router-view />
	</div>
	<div v-else class="content-wrapper">
		<div class="container flex-grow-1 container-p-y">

			<div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-6 row-gap-4">
				<div class="d-flex flex-column justify-content-center">
					<h4 class="mb-1">{{ $t('microLearning.pageTitle') }}</h4>
					<p class="mb-0">{{ $t('microLearning.pageDescription') }}</p>
				</div>
				<div class="d-flex align-content-center flex-wrap gap-3">
					<router-link to="/micro-learning/modules">
						<button type="button" class="btn btn-outline-primary">
							<i class="ri-settings-3-line me-1"></i>{{ $t('microLearning.manageModules') }}
						</button>
					</router-link>
					<button type="button" class="btn btn-primary" @click="openAssignModal">
						<i class="ri-user-add-line me-1"></i>{{ $t('microLearning.assignModule') }}
					</button>
				</div>
			</div>

			<!-- Metric Cards -->
			<div class="row g-6 mb-6">
				<div class="col-12 col-sm-4">
					<div class="card h-100">
						<div class="card-body">
							<div class="d-flex align-items-center gap-3">
								<div class="avatar">
									<span class="avatar-initial rounded bg-label-primary">
										<i class="ri-group-line ri-24px"></i>
									</span>
								</div>
								<div>
									<h4 class="mb-0">{{ stats.totalEmployees }}</h4>
									<small class="text-muted">{{ $t('microLearning.dashboard.totalEmployees') }}</small>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div class="col-12 col-sm-4">
					<div class="card h-100">
						<div class="card-body">
							<div class="d-flex align-items-center gap-3">
								<div class="avatar">
									<span class="avatar-initial rounded bg-label-info">
										<i class="ri-send-plane-line ri-24px"></i>
									</span>
								</div>
								<div>
									<h4 class="mb-0">{{ stats.totalSent }}</h4>
									<small class="text-muted">{{ $t('microLearning.dashboard.totalSent') }}</small>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div class="col-12 col-sm-4">
					<div class="card h-100">
						<div class="card-body">
							<div class="d-flex align-items-center gap-3">
								<div class="avatar">
									<span class="avatar-initial rounded" :class="stats.completionRate > 70 ? 'bg-label-success' : stats.completionRate > 40 ? 'bg-label-warning' : 'bg-label-danger'">
										<i class="ri-pie-chart-line ri-24px"></i>
									</span>
								</div>
								<div>
									<h4 class="mb-0">{{ stats.completionRate }}%</h4>
									<small class="text-muted">{{ $t('microLearning.dashboard.completionRate') }}</small>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Tab Switcher + Filter -->
			<div class="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
				<div class="d-flex gap-3">
					<button
						class="tab-pill"
						:class="{ active: activeTab === 'inProgress' }"
						@click="activeTab = 'inProgress'"
					>
						<i class="ri-play-circle-line"></i>
						{{ $t('microLearning.dashboard.inProgressTitle') }}
						<span class="tab-pill-count">{{ filteredInProgress.length }}</span>
					</button>
					<button
						class="tab-pill"
						:class="{ active: activeTab === 'lowScore' }"
						@click="activeTab = 'lowScore'"
					>
						<i class="ri-alert-line"></i>
						{{ $t('microLearning.dashboard.lowScoreTitle') }}
						<span class="tab-pill-count tab-pill-count-danger">{{ filteredLowScore.length }}</span>
					</button>
				</div>
				<div class="d-flex align-items-center gap-3">
					<select class="form-select form-select-sm" style="min-width: 180px;" v-model="dashboardModuleFilter" @change="applyDashboardFilter">
						<option value="">{{ $t('microLearning.assignments.allModules') }}</option>
						<option v-for="mod in modulesList" :key="mod._id" :value="mod._id">{{ mod.name }}</option>
					</select>
					<router-link to="/micro-learning/assignments" class="btn btn-sm btn-outline-primary text-nowrap">
						{{ $t('microLearning.dashboard.viewAll') }}<i class="ri-arrow-right-s-line ms-1"></i>
					</router-link>
				</div>
			</div>

			<!-- Table Section -->
			<div class="card">

				<!-- In Progress Tab -->
				<div v-show="activeTab === 'inProgress'">
					<div class="table-responsive" v-if="filteredInProgress.length > 0">
						<table class="table table-borderless mb-0">
							<thead>
								<tr class="border-bottom">
									<th class="bg-transparent fs-xsmall">{{ $t('microLearning.assignments.employee') }}</th>
									<th class="bg-transparent fs-xsmall">{{ $t('microLearning.assignments.module') }}</th>
									<th class="bg-transparent fs-xsmall">{{ $t('microLearning.assignments.status') }}</th>
									<th class="bg-transparent fs-xsmall">{{ $t('microLearning.assignments.date') }}</th>
									<th class="bg-transparent fs-xsmall">{{ $t('common.fields.actions') }}</th>
								</tr>
							</thead>
							<tbody class="table-border-bottom-0">
								<tr v-for="assignment in filteredInProgress" :key="assignment._id">
									<td>
										<div class="d-flex align-items-center gap-2">
											<div class="avatar avatar-sm">
												<span class="avatar-initial rounded-circle bg-label-primary">{{ initials(assignment.user) }}</span>
											</div>
											<div>
												<span class="fw-medium">{{ assignment.user.firstName }} {{ assignment.user.lastName }}</span>
												<br />
												<small class="text-muted">{{ assignment.user.email }}</small>
											</div>
										</div>
									</td>
									<td>
										<span class="fw-medium">{{ assignment.module.name }}</span>
									</td>
									<td>
										<span class="badge rounded-pill" :class="statusBadgeClass(assignment.status)">
											{{ statusLabel(assignment.status) }}
										</span>
									</td>
									<td>
										<span class="fw-medium">{{ formatDate(assignment.createdAt) }}</span>
									</td>
									<td>
										<a href="javascript:void(0);" class="btn btn-sm btn-icon btn-text-secondary rounded-pill me-1 waves-effect waves-light" data-bs-toggle="tooltip" :data-bs-title="$t('microLearning.assignments.viewDetails')" @click="viewDetails(assignment._id)">
											<i class="ri-eye-line ri-22px"></i>
										</a>
										<a :href="assignment.link" target="_blank" class="btn btn-sm btn-icon btn-text-secondary rounded-pill me-1 waves-effect waves-light" data-bs-toggle="tooltip" :data-bs-title="$t('microLearning.assignments.openLink')">
											<i class="ri-external-link-line ri-22px"></i>
										</a>
										<a href="javascript:void(0);" class="btn btn-sm btn-icon btn-text-secondary rounded-pill me-1 waves-effect waves-light" data-bs-toggle="tooltip" :data-bs-title="$t('microLearning.assignments.sendSms')" @click="openSmsModal(assignment)">
											<i class="ri-message-2-line ri-22px"></i>
										</a>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
					<div v-else class="text-center py-6 px-4">
						<i class="ri-inbox-line ri-48px text-muted mb-3 d-block"></i>
						<h6 class="mb-2">{{ $t('microLearning.dashboard.noInProgress') }}</h6>
						<p class="text-muted small mb-4">{{ $t('microLearning.dashboard.noInProgressHint') }}</p>
						<button type="button" class="btn btn-sm btn-primary" @click="openAssignModal">
							<i class="ri-user-add-line me-1"></i>{{ $t('microLearning.assignModule') }}
						</button>
					</div>
				</div>

				<!-- Low Score Tab -->
				<div v-show="activeTab === 'lowScore'">
					<div class="table-responsive" v-if="filteredLowScore.length > 0">
						<table class="table table-borderless mb-0">
							<thead>
								<tr class="border-bottom">
									<th class="bg-transparent fs-xsmall">{{ $t('microLearning.assignments.employee') }}</th>
									<th class="bg-transparent fs-xsmall">{{ $t('microLearning.assignments.module') }}</th>
									<th class="bg-transparent fs-xsmall">{{ $t('microLearning.assignments.score') }}</th>
									<th class="bg-transparent fs-xsmall">{{ $t('microLearning.assignments.date') }}</th>
									<th class="bg-transparent fs-xsmall">{{ $t('common.fields.actions') }}</th>
								</tr>
							</thead>
							<tbody class="table-border-bottom-0">
								<tr v-for="assignment in filteredLowScore" :key="assignment._id">
									<td>
										<div class="d-flex align-items-center gap-2">
											<div class="avatar avatar-sm">
												<span class="avatar-initial rounded-circle bg-label-danger">{{ initials(assignment.user) }}</span>
											</div>
											<div>
												<span class="fw-medium">{{ assignment.user.firstName }} {{ assignment.user.lastName }}</span>
												<br />
												<small class="text-muted">{{ assignment.user.email }}</small>
											</div>
										</div>
									</td>
									<td>
										<span class="fw-medium">{{ assignment.module.name }}</span>
									</td>
									<td>
										<div class="score-circle" :style="scoreCircleStyle(assignment.score)">
											<span class="score-value">{{ assignment.score }}%</span>
										</div>
									</td>
									<td>
										<span class="fw-medium">{{ formatDate(assignment.completedAt) }}</span>
									</td>
									<td>
										<a href="javascript:void(0);" class="btn btn-sm btn-icon btn-text-secondary rounded-pill me-1 waves-effect waves-light" data-bs-toggle="tooltip" :data-bs-title="$t('microLearning.assignments.viewDetails')" @click="viewDetails(assignment._id)">
											<i class="ri-eye-line ri-22px"></i>
										</a>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
					<div v-else class="text-center py-6 px-4">
						<i class="ri-checkbox-circle-line ri-48px text-success mb-3 d-block"></i>
						<h6 class="mb-2">{{ $t('microLearning.dashboard.noLowScore') }}</h6>
						<p class="text-muted small mb-0">{{ $t('microLearning.dashboard.noLowScoreHint') }}</p>
					</div>
				</div>
			</div>
		</div>

		<!-- ASSIGN MODAL -->
		<div class="modal fade" id="assignModuleModal" tabindex="-1" aria-hidden="true">
			<div class="modal-dialog modal-dialog-centered modal-simple">
				<div class="modal-content">
					<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>

					<div class="modal-body p-0">
						<div class="text-center mb-6">
							<h4 class="mb-2">{{ $t('microLearning.assignModule') }}</h4>
						</div>

						<!-- Module selection -->
						<div class="mb-4">
							<div class="form-floating form-floating-outline">
								<select class="form-select" v-model="assignModalModuleId">
									<option value="" disabled>{{ $t('microLearning.assignModal.selectModule') }}</option>
									<option v-for="mod in modulesList" :key="mod._id" :value="mod._id">{{ mod.name }}</option>
								</select>
								<label>{{ $t('microLearning.assignments.module') }}</label>
							</div>
						</div>

						<!-- Employee search -->
						<div class="mb-4">
							<div class="form-floating form-floating-outline">
								<input
									type="text"
									class="form-control"
									:placeholder="$t('microLearning.assignModal.searchEmployees')"
									v-model="employeeSearch"
									@input="searchEmployeesDebounced"
									autocomplete="off"
								/>
								<label>{{ $t('microLearning.assignModal.searchEmployees') }}</label>
							</div>
						</div>

						<div class="mb-4" v-if="selectedEmployeeIds.length > 0">
							<small class="text-muted">{{ $t('microLearning.assignModal.selectedCount', { count: selectedEmployeeIds.length }) }}</small>
						</div>

						<div style="max-height: 300px; overflow-y: auto;">
							<div v-for="employee in availableEmployees" :key="employee._id" class="form-check mb-3">
								<input
									class="form-check-input"
									type="checkbox"
									:id="'emp-' + employee._id"
									:value="employee._id"
									v-model="selectedEmployeeIds"
									:disabled="alreadyAssignedIds.includes(employee._id)"
								/>
								<label class="form-check-label" :for="'emp-' + employee._id" :class="{ 'text-muted': alreadyAssignedIds.includes(employee._id) }">
									{{ employee.firstName }} {{ employee.lastName }}
									<small class="text-muted d-block">{{ employee.email }}</small>
									<span class="badge bg-label-secondary" v-if="alreadyAssignedIds.includes(employee._id)">{{ $t('microLearning.assignModal.alreadyAssigned') }}</span>
								</label>
							</div>
							<div v-if="availableEmployees.length === 0" class="text-center text-muted py-3">
								{{ $t('microLearning.assignModal.noEmployees') }}
							</div>
						</div>

						<div class="col-12 d-flex flex-wrap justify-content-center gap-4 row-gap-4 mt-5">
							<button type="button" class="btn btn-primary" @click="assignModule()" :disabled="selectedEmployeeIds.length === 0 || !assignModalModuleId">
								{{ $t('microLearning.assignModal.assign') }}
							</button>
							<button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">
								{{ $t('common.actions.cancel') }}
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- SMS MODAL -->
		<div class="modal fade" id="smsModalHome" tabindex="-1" aria-hidden="true">
			<div class="modal-dialog modal-dialog-centered modal-simple">
				<div class="modal-content">
					<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					<div class="modal-body p-0" v-if="!smsSent">
						<div class="text-center mb-6">
							<i class="ri-message-2-line" style="font-size: 35px; color: #666cff;"></i>
							<h4 class="mt-3 mb-2">{{ $t('microLearning.smsModal.title') }}</h4>
							<p class="text-muted small" v-if="smsAssignment">{{ smsAssignment.user.firstName }} {{ smsAssignment.user.lastName }}</p>
						</div>

						<div class="mb-4">
							<label class="form-label">{{ $t('microLearning.smsModal.phoneLabel') }}</label>
							<div class="input-group">
								<select class="form-select" style="max-width: 120px;" v-model="smsCountryCode">
									<option value="+33">🇫🇷 +33</option>
									<option value="+1">🇺🇸 +1</option>
									<option value="+44">🇬🇧 +44</option>
									<option value="+34">🇪🇸 +34</option>
									<option value="+49">🇩🇪 +49</option>
									<option value="+39">🇮🇹 +39</option>
									<option value="+32">🇧🇪 +32</option>
									<option value="+41">🇨🇭 +41</option>
									<option value="+352">🇱🇺 +352</option>
									<option value="+966">🇸🇦 +966</option>
									<option value="+212">🇲🇦 +212</option>
									<option value="+216">🇹🇳 +216</option>
									<option value="+213">🇩🇿 +213</option>
								</select>
								<input
									type="tel"
									class="form-control"
									:placeholder="$t('microLearning.smsModal.phonePlaceholder')"
									v-model="smsPhone"
									autocomplete="off"
								/>
							</div>
						</div>

						<div class="col-12 d-flex flex-wrap justify-content-center gap-4 row-gap-4">
							<button type="button" class="btn btn-primary" @click="sendSms()" :disabled="!smsPhone || isSendingSms">
								<i class="ri-send-plane-line me-1"></i>{{ $t('microLearning.smsModal.send') }}
							</button>
							<button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">
								{{ $t('common.actions.cancel') }}
							</button>
						</div>
					</div>
					<div class="modal-body p-0" v-if="smsSent">
						<div class="text-center">
							<CheckIcon />
							<p class="text-muted mt-5 small">{{ $t('microLearning.smsModal.success') }}</p>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- ASSIGN SUCCESS MODAL -->
		<div class="modal fade" id="assignSuccessModal" tabindex="-1" aria-hidden="true">
			<div class="modal-dialog modal-dialog-centered modal-simple modal-add-new-cc">
				<div class="modal-content">
					<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					<div class="modal-body p-0">
						<div class="text-center">
							<CheckIcon />
							<p class="text-muted mt-5 small">{{ $t('microLearning.assignModal.assignSuccess') }}</p>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- FEATURE INTRO MODAL -->
		<div class="modal fade" id="microLearningIntroModal" tabindex="-1" aria-hidden="true">
			<div class="modal-dialog modal-dialog-centered modal-simple modal-add-new-cc">
				<div class="modal-content">
					<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					<div class="modal-body p-0">
						<div class="text-center">
							<i class="ri-lightbulb-line" style="font-size: 45px; color: #666cff;"></i>
							<h4 class="mt-4 mb-2">{{ $t('microLearning.intro.title') }}</h4>
							<p class="text-muted">{{ $t('microLearning.intro.desc1') }}</p>
							<p class="text-muted">{{ $t('microLearning.intro.desc2') }}</p>
							<p class="text-muted">{{ $t('microLearning.intro.desc3') }}</p>
							<button class="btn btn-primary mt-2" data-bs-dismiss="modal">{{ $t('microLearning.intro.gotIt') }}</button>
						</div>
					</div>
				</div>
			</div>
		</div>

		<div class="content-backdrop fade"></div>
	</div>
</template>

<script>
import HTTP from "@/axios";
import { Tooltip } from "bootstrap";
import CheckIcon from "@/components/CheckIcon";

export default {
	name: "MicroLearningIndex",

	components: { CheckIcon },

	data() {
		return {
			isLoading: false,
			hasCompletedAssignments: false,
			activeTab: "inProgress",
			dashboardModuleFilter: "",
			inProgressAssignments: [],
			lowScoreAssignments: [],
			modulesList: [],
			stats: {
				totalEmployees: 0,
				totalSent: 0,
				completionRate: 0,
			},

			// SMS modal
			smsAssignment: null,
			smsCountryCode: "+33",
			smsPhone: "",
			isSendingSms: false,
			smsSent: false,

			// Assign modal
			assignModalModuleId: "",
			employeeSearch: "",
			availableEmployees: [],
			selectedEmployeeIds: [],
			alreadyAssignedIds: [],
			searchTimeout: null,
		};
	},

	computed: {
		isChildRoute() {
			return this.$route.name && this.$route.name !== "micro-learning";
		},
		filteredInProgress() {
			if (!this.dashboardModuleFilter) return this.inProgressAssignments;
			return this.inProgressAssignments.filter(a => a.module._id === this.dashboardModuleFilter);
		},
		filteredLowScore() {
			if (!this.dashboardModuleFilter) return this.lowScoreAssignments;
			return this.lowScoreAssignments.filter(a => a.module._id === this.dashboardModuleFilter);
		},
	},

	methods: {
		applyDashboardFilter() {
			// Computed properties handle filtering reactively
		},

		viewDetails(assignmentId) {
			this.$router.push(`/micro-learning/assignments/${assignmentId}`);
		},

		async fetchDashboard() {
			this.isLoading = true;
			try {
				// Fetch all assignments to compute stats + low scores
				const response = await HTTP.get("/micro-learning/assignments", { params: { limit: 200 } });
				const all = response.data.assignments;

				// Stats
				const completed = all.filter(a => a.status === "completed");
				this.hasCompletedAssignments = completed.length > 0;
				this.stats.totalSent = all.length;
				this.stats.completionRate = all.length > 0
					? Math.round((completed.length / all.length) * 100)
					: 0;

				// In progress: pending + started
				this.inProgressAssignments = all
					.filter(a => a.status === "pending" || a.status === "started")
					.slice(0, 10);

				// Low score: completed with score <= 70, sorted ascending
				this.lowScoreAssignments = completed
					.filter(a => a.score <= 70)
					.sort((a, b) => a.score - b.score)
					.slice(0, 10);

				this.$nextTick(() => {
					const tooltipTriggerList = this.$el.querySelectorAll('[data-bs-toggle="tooltip"]');
					tooltipTriggerList.forEach((el) => {
						new Tooltip(el);
					});
				});
			} catch (error) {
				const errorMessage = error?.response?.data?.message || this.$t("errors.generic");
				alert(errorMessage);
				console.error("Erreur API:", error);
			} finally {
				this.isLoading = false;
			}
		},

		async fetchEmployeeCount() {
			try {
				const response = await HTTP.get("/company/employees", { params: { page: 1, limit: 1 } });
				this.stats.totalEmployees = response.data.pagination.totalEntries;
			} catch (error) {
				console.error("Erreur API:", error);
			}
		},

		async fetchModulesList() {
			try {
				const response = await HTTP.get("/micro-learning/modules");
				this.modulesList = response.data.modules;
			} catch (error) {
				console.error("Erreur API:", error);
			}
		},

		// ─── SMS Modal ──────────────────────────────────────────────
		openSmsModal(assignment) {
			this.smsAssignment = assignment;
			this.smsCountryCode = "+33";
			this.smsPhone = assignment.user.phone || "";
			this.smsSent = false;
			this.isSendingSms = false;

			this.$nextTick(() => {
				const el = document.getElementById("smsModalHome");
				let modal = window.bootstrap?.Modal?.getInstance(el);
				if (!modal) {
					modal = new window.bootstrap.Modal(el);
				}
				modal.show();
			});
		},

		async sendSms() {
			if (!this.smsPhone || !this.smsAssignment) return;
			this.isSendingSms = true;

			try {
				await HTTP.post(`/micro-learning/assignments/${this.smsAssignment._id}/send-sms`, {
					phone: this.smsCountryCode + this.smsPhone,
				});
				this.smsSent = true;
				setTimeout(() => {
					const modal = window.bootstrap.Modal.getInstance(document.getElementById("smsModalHome"));
					if (modal) modal.hide();
				}, 1500);
			} catch (error) {
				const errorMessage = error?.response?.data?.message || this.$t("errors.generic");
				alert(errorMessage);
				console.error("Erreur API:", error);
			} finally {
				this.isSendingSms = false;
			}
		},

		// ─── Assign Modal ───────────────────────────────────────────
		openAssignModal() {
			this.assignModalModuleId = "";
			this.employeeSearch = "";
			this.selectedEmployeeIds = [];
			this.alreadyAssignedIds = [];
			this.availableEmployees = [];

			this.fetchEmployees();

			this.$nextTick(() => {
				const modalElement = document.getElementById("assignModuleModal");
				let modal = window.bootstrap?.Modal?.getInstance(modalElement);
				if (!modal) {
					modal = new window.bootstrap.Modal(modalElement);
				}
				modal.show();
			});
		},

		async fetchEmployees() {
			try {
				const params = {
					page: 1,
					limit: 30,
					search: this.employeeSearch || undefined,
				};
				const response = await HTTP.get("/company/employees", { params });
				this.availableEmployees = response.data.employees;
			} catch (error) {
				console.error("Erreur API:", error);
			}
		},

		searchEmployeesDebounced() {
			if (this.searchTimeout) clearTimeout(this.searchTimeout);
			this.searchTimeout = setTimeout(() => {
				this.fetchEmployees();
			}, 300);
		},

		async assignModule() {
			if (this.selectedEmployeeIds.length === 0 || !this.assignModalModuleId) return;

			try {
				await HTTP.post("/micro-learning/assign", {
					moduleId: this.assignModalModuleId,
					employeeIds: this.selectedEmployeeIds,
				});

				const assignModal = window.bootstrap.Modal.getInstance(document.getElementById("assignModuleModal"));
				if (assignModal) assignModal.hide();

				this.fetchDashboard();

				// Show success modal
				this.$nextTick(() => {
					const successModal = new window.bootstrap.Modal(document.getElementById("assignSuccessModal"));
					successModal.show();
					setTimeout(() => {
						const instance = window.bootstrap.Modal.getInstance(document.getElementById("assignSuccessModal"));
						if (instance) instance.hide();
					}, 2000);
				});
			} catch (error) {
				if (error?.response?.status === 403) {
					alert(this.$t("microLearning.assignModal.assignError403"));
				} else {
					const errorMessage = error?.response?.data?.message || this.$t("errors.generic");
					alert(errorMessage);
				}
				console.error("Erreur API:", error);
			}
		},

		// ─── Intro ──────────────────────────────────────────────────
		showIntroIfNeeded() {
			const key = "pika_micro_learning_intro_seen";
			if (!localStorage.getItem(key)) {
				setTimeout(() => {
					const el = document.getElementById("microLearningIntroModal");
					if (el && window.bootstrap) {
						const modal = new window.bootstrap.Modal(el);
						modal.show();
						localStorage.setItem(key, "true");
					}
				}, 800);
			}
		},

		// ─── Helpers ────────────────────────────────────────────────
		statusBadgeClass(status) {
			const map = { pending: "bg-label-primary", started: "bg-label-info", completed: "bg-label-success" };
			return map[status] || "bg-label-secondary";
		},

		statusLabel(status) {
			return this.$t(`microLearning.assignments.${status}`);
		},

		initials(user) {
			const f = user.firstName ? user.firstName.charAt(0).toUpperCase() : "";
			const l = user.lastName ? user.lastName.charAt(0).toUpperCase() : "";
			return `${f}${l}`;
		},

		scoreColor(score) {
			if (score <= 40) return "#dc3545";
			if (score <= 70) return "#f59e0b";
			return "#10b981";
		},

		scoreCircleStyle(score) {
			const color = this.scoreColor(score);
			return {
				background: `conic-gradient(${color} ${score * 3.6}deg, #e9ecef ${score * 3.6}deg)`,
			};
		},

		scoreClass(score) {
			if (score <= 40) return "text-danger";
			if (score <= 70) return "text-warning";
			return "text-success";
		},

		formatDate(dateStr) {
			if (!dateStr) return "-";
			const d = new Date(dateStr);
			return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
		},
	},

	watch: {
		'$route.name'(newName) {
			if (newName === 'micro-learning') {
				this.fetchDashboard();
				this.fetchModulesList();
			}
		},
	},

	async mounted() {
		if (this.isChildRoute) return;
		this.showIntroIfNeeded();
		await Promise.all([this.fetchDashboard(), this.fetchEmployeeCount(), this.fetchModulesList()]);
	},
};
</script>

<style scoped>
.form-check-label {
	cursor: pointer;
}

/* ── TAB PILLS ── */
.tab-pill {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	padding: 8px 18px;
	border: 1.5px solid #e0e0e0;
	background: #fff;
	border-radius: 50px;
	color: #8592a3;
	font-size: 13px;
	font-weight: 500;
	cursor: pointer;
	transition: all .2s ease;
}

.tab-pill:hover {
	border-color: #c0c0c0;
	color: #566a7f;
}

.tab-pill.active {
	border-color: #696cff;
	background: rgba(105, 108, 255, .06);
	color: #696cff;
}

.tab-pill i {
	font-size: 16px;
}

.tab-pill-count {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 20px;
	height: 20px;
	padding: 0 6px;
	border-radius: 20px;
	font-size: 11px;
	font-weight: 700;
	background: rgba(105, 108, 255, .12);
	color: #696cff;
}

.tab-pill-count-danger {
	background: rgba(234, 84, 85, .12);
	color: #ea5455;
}

.score-circle {
	width: 44px;
	height: 44px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	position: relative;
}

.score-circle::before {
	content: '';
	position: absolute;
	width: 32px;
	height: 32px;
	border-radius: 50%;
	background: #fff;
}

.score-value {
	position: relative;
	font-size: 0.7rem;
	font-weight: 600;
}
</style>
